import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Sparkles, ChevronRight, Users } from 'lucide-react';

const ModelSelector = ({ 
  models, 
  selectedModel, 
  onModelChange, 
  disabled = false,
  burstGroups = [],
  selectedBurstGroup = null,
  onBurstGroupChange = null,
  burstMode = false,
  onModeChange = null,
  topModelsConfig = []
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showBurstModels, setShowBurstModels] = useState(false);
  const [showMoreModels, setShowMoreModels] = useState(false);
  const [burstModelsPosition, setBurstModelsPosition] = useState({ bottom: 0, left: 0 });
  const [moreModelsPosition, setMoreModelsPosition] = useState({ bottom: 0, left: 0 });
  const containerRef = useRef(null);
  const burstModelsRef = useRef(null);
  const moreModelsRef = useRef(null);
  const burstModelsTimeoutRef = useRef(null);
  const moreModelsTimeoutRef = useRef(null);


  // Calculate position for Burst Models dropdown - goes UP from bottom of hovered element
  const calculateBurstModelsPosition = () => {
    if (burstModelsRef.current) {
      const rect = burstModelsRef.current.getBoundingClientRect();
      setBurstModelsPosition({
        bottom: window.innerHeight - rect.bottom, // Start from bottom of hovered element
        left: rect.right - 1 // 1px overlap to ensure seamless hover
      });
    }
  };

  // Calculate position for More Models dropdown - goes UP from bottom of hovered element
  const calculateMoreModelsPosition = () => {
    if (moreModelsRef.current) {
      const rect = moreModelsRef.current.getBoundingClientRect();
      setMoreModelsPosition({
        bottom: window.innerHeight - rect.bottom, // Start from bottom of hovered element
        left: rect.right - 1 // 1px overlap to ensure seamless hover
      });
    }
  };

  // Update positions when dropdowns are shown

  useEffect(() => {
    if (showBurstModels) {
      calculateBurstModelsPosition();
    }
  }, [showBurstModels]);

  useEffect(() => {
    if (showMoreModels) {
      calculateMoreModelsPosition();
    }
  }, [showMoreModels]);

  // Delayed hide functions for smoother interaction

  const handleBurstModelsLeave = () => {
    burstModelsTimeoutRef.current = setTimeout(() => {
      setShowBurstModels(false);
    }, 150); // 150ms delay
  };

  const handleBurstModelsEnter = () => {
    if (burstModelsTimeoutRef.current) {
      clearTimeout(burstModelsTimeoutRef.current);
    }
    calculateBurstModelsPosition();
    setShowBurstModels(true);
  };

  const handleMoreModelsLeave = () => {
    moreModelsTimeoutRef.current = setTimeout(() => {
      setShowMoreModels(false);
    }, 150); // 150ms delay
  };

  const handleMoreModelsEnter = () => {
    if (moreModelsTimeoutRef.current) {
      clearTimeout(moreModelsTimeoutRef.current);
    }
    calculateMoreModelsPosition();
    setShowMoreModels(true);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (burstModelsTimeoutRef.current) {
        clearTimeout(burstModelsTimeoutRef.current);
      }
      if (moreModelsTimeoutRef.current) {
        clearTimeout(moreModelsTimeoutRef.current);
      }
    };
  }, []);

  // Get all models organized by provider
  const getAllModels = () => {
    return [
      ...(models.openai || []),
      ...(models.anthropic || []),
      ...(models.google || []),
      ...(models.xai || [])
    ];
  };

  const allAvailableModels = getAllModels();

  // Separate top models from other models
  const getTopModels = () => {
    if (!topModelsConfig || topModelsConfig.length === 0) return [];
    return allAvailableModels.filter(model => topModelsConfig.includes(model.id));
  };

  const getOtherModels = () => {
    if (!topModelsConfig || topModelsConfig.length === 0) return allAvailableModels;
    return allAvailableModels.filter(model => !topModelsConfig.includes(model.id));
  };

  const topModels = getTopModels();
  const otherModels = getOtherModels();

  // Get all models organized by provider
  const allModels = [
    ...(models.openai || []).map(model => ({ ...model, provider: 'OpenAI' })),
    ...(models.anthropic || []).map(model => ({ ...model, provider: 'Anthropic' })),
    ...(models.google || []).map(model => ({ ...model, provider: 'Google' })),
    ...(models.xai || []).map(model => ({ ...model, provider: 'xAI' }))
  ];

  // No longer need to filter remaining models since we show all models directly

  const selectedModelData = allModels.find(model => model.id === selectedModel);

  const handleModelSelect = (modelId) => {
    onModelChange(modelId);
    if (onModeChange) onModeChange(false); // Switch to single mode
    setIsOpen(false);
    setShowBurstModels(false);
    setShowMoreModels(false);
  };

  const handleBurstGroupSelect = (burstGroup) => {
    if (onBurstGroupChange) onBurstGroupChange(burstGroup);
    if (onModeChange) onModeChange(true); // Switch to burst mode
    setIsOpen(false);
    setShowBurstModels(false);
    setShowMoreModels(false);
  };

  const getDisplayName = (model) => {
    if (!model) return '';
    const name = model.name.toLowerCase();

    // Simplified names for top models
    if (name.includes('o3-mini')) return 'OpenAI o3-mini';
    if (name.includes('o3')) return 'OpenAI o3';
    if (name.includes('gpt-4o-mini')) return 'OpenAI 4o-mini';
    if (name.includes('gpt-4o')) return 'OpenAI 4o';
    if (name.includes('claude') && name.includes('4.5') && name.includes('sonnet')) return 'Claude Sonnet 4.5';
    if (name.includes('claude-3-5-sonnet')) return 'Claude Sonnet 3.5';
    if (name.includes('claude-3-opus')) return 'Claude Opus 3';
    if (name.includes('gemini-2.0-flash')) return 'Gemini 2.0 Flash';
    if (name.includes('gemini-1.5-pro')) return 'Gemini 1.5 Pro';
    if (name.includes('grok-4')) return 'Grok 4';
    if (name.includes('grok-beta')) return 'Grok Beta';

    return model.name;
  };

  const getDisplayText = () => {
    if (burstMode && selectedBurstGroup) {
      return selectedBurstGroup.name;
    }
    return getDisplayName(selectedModelData) || selectedModel || 'Select Model';
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Right-side dropdowns positioned outside main dropdown */}

      {/* More Models right-side dropdown */}
      {isOpen && showMoreModels && otherModels.length > 0 && (
        <div
          className="fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[100] max-h-64 overflow-y-auto w-80"
          style={{
            left: moreModelsPosition.left,
            bottom: moreModelsPosition.bottom,
          }}
          onMouseEnter={handleMoreModelsEnter}
          onMouseLeave={handleMoreModelsLeave}
        >
          <div className="py-1">
            <div className="px-3 py-2 text-xs font-semibold text-blue-600 uppercase tracking-wider bg-blue-50">
              More Models
            </div>
            {otherModels.map((model) => {
              const provider = model.provider ||
                (models.openai?.includes(model) ? 'OpenAI' :
                 models.anthropic?.includes(model) ? 'Anthropic' :
                 models.google?.includes(model) ? 'Google' :
                 models.xai?.includes(model) ? 'xAI' : 'Unknown');

              return (
                <button
                  key={model.id}
                  onClick={() => handleModelSelect(model.id)}
                  className={`
                    w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between
                    ${!burstMode && selectedModel === model.id ? 'bg-claude-accent bg-opacity-10 text-claude-accent' : 'text-claude-text'}
                  `}
                >
                  <span>{getDisplayName(model)}</span>
                  <span className="text-xs text-gray-500">{provider}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Burst Models right-side dropdown */}
      {isOpen && showBurstModels && burstGroups.length > 0 && (
        <div 
          className="fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[100] max-h-64 overflow-y-auto w-80"
          style={{ 
            left: burstModelsPosition.left,
            bottom: burstModelsPosition.bottom,
          }}
          onMouseEnter={handleBurstModelsEnter}
          onMouseLeave={handleBurstModelsLeave}
        >
          <div className="py-1">
            <div className="px-3 py-2 text-xs font-semibold text-purple-600 uppercase tracking-wider bg-purple-50">
              Burst Groups
            </div>
            {burstGroups.map((group) => (
              <button
                key={group.id}
                onClick={() => handleBurstGroupSelect(group)}
                className={`
                  w-full text-left px-3 py-2 text-sm hover:bg-gray-50
                  ${burstMode && selectedBurstGroup?.id === group.id ? 'bg-purple-100 text-purple-700' : 'text-claude-text'}
                `}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{group.name}</span>
                  <span className="text-xs text-gray-500">{group.models?.length || 0} models</span>
                </div>
                {group.description && (
                  <div className="text-xs text-gray-500 mt-1">{group.description}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center justify-between w-full px-3 py-2 text-sm 
          border border-claude-border rounded-lg bg-white hover:bg-gray-50
          focus:outline-none focus:ring-2 focus:ring-claude-accent focus:ring-opacity-50
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <div className="flex items-center gap-2">
          {burstMode && <Users className="w-4 h-4 text-purple-600" />}
          <span className="text-claude-text text-sm truncate">
            {getDisplayText()}
          </span>
        </div>
        <ChevronDown 
          className={`w-4 h-4 text-claude-muted transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-claude-border rounded-lg shadow-lg z-40 max-h-80">
          <div className="py-1 max-h-80 overflow-y-auto overflow-x-visible">
            {/* Models Section */}
            <div className="px-3 py-2 text-xs font-semibold text-claude-muted uppercase tracking-wider bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-100">
              <div className="flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Models
              </div>
            </div>
            
            {/* Show top models first */}
            {topModels.map(model => {
              const provider = model.provider ||
                (models.openai?.includes(model) ? 'OpenAI' :
                 models.anthropic?.includes(model) ? 'Anthropic' :
                 models.google?.includes(model) ? 'Google' :
                 models.xai?.includes(model) ? 'xAI' : 'Unknown');

              return (
                <button
                  key={model.id}
                  onClick={() => handleModelSelect(model.id)}
                  className={`
                    w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between
                    ${!burstMode && selectedModel === model.id ? 'bg-claude-accent bg-opacity-10 text-claude-accent' : 'text-claude-text'}
                  `}
                >
                  <span>{getDisplayName(model)}</span>
                  <span className="text-xs text-gray-500">{provider}</span>
                </button>
              );
            })}

            {/* More Models with Right Dropdown */}
            {otherModels.length > 0 && (
              <div className="relative">
                <button
                  ref={moreModelsRef}
                  onMouseEnter={handleMoreModelsEnter}
                  onMouseLeave={handleMoreModelsLeave}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-claude-muted flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-blue-600" />
                    <span>More Models</span>
                  </div>
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* Burst Models with Right Dropdown */}
            {burstGroups.length > 0 && (
              <div className="relative">
                <button
                  ref={burstModelsRef}
                  onMouseEnter={handleBurstModelsEnter}
                  onMouseLeave={handleBurstModelsLeave}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-claude-muted flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-3 h-3 text-purple-600" />
                    <span>Burst Models</span>
                  </div>
                  <ChevronRight className="w-3 h-3" />
                </button>

              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelSelector;